import { authenticatedFetch } from "../api/authenticatedFetch";

// Three facts about authenticatedFetch shape this file. Read
// `src/api/authenticatedFetch.js` once before changing anything here.
//
// 1. It returns the raw Response, not parsed JSON -- so every call ends in
//    an explicit `res.json()`. (`authenticatedGet`/`authenticatedPost` do
//    parse, but they also build their own URLs and swallow the error body,
//    which loses the field name below.)
// 2. It THROWS on any non-2xx before the caller ever sees the Response
//    (`authenticatedFetch.js:139-148`), with `message` already lifted from
//    `error.message` and the parsed body kept on `err.serverBody`. So the
//    only work left on the error path is lifting `error.field` onto the
//    error -- the create form reads `err.field` to mark the offending
//    input. An `if (!res.ok)` branch here would be dead code.
// 3. It JSON-stringifies an object `body` itself
//    (`authenticatedFetch.js:60-63`), so these calls pass plain objects and
//    must NOT pre-stringify.
//
// And the Go writeJSON helper wraps every payload under a named key, so a
// successful body is `{tenants: [...]}`, never the array itself. Returning
// `body` instead of `body[key]` renders an empty page with no error.
const call = async (url, options, key) => {
	let res;
	try {
		res = await authenticatedFetch(url, options);
	} catch (err) {
		const envelope = err.serverBody?.error;
		err.field = envelope?.field || null;
		// On an unexpected failure the platform envelope also carries `detail`:
		// the raw error text, which for these screens IS the answer ("read
		// dataset acme: permission denied") where err.message is only the
		// generic "Something went wrong". Compose it once here so every caller
		// that renders err.message shows it without a change of its own.
		err.detail = envelope?.detail || null;
		// A refused activation (409) carries the nine-check report that
		// refused it. Lifting it here means every caller that already renders
		// err.message can also render WHAT is missing, without the detail page
		// re-fetching a report the server just computed.
		err.readiness = envelope?.readiness || null;
		if (err.detail && !err.message?.includes(err.detail)) {
			err.message = `${err.message}: ${err.detail}`;
		}
		throw err;
	}
	const body = await res.json().catch(() => ({}));
	return body[key];
};

export const listTenants = () =>
	call("/api/v1/platform/tenants", undefined, "tenants");

export const getTenant = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}`, undefined, "tenant");

export const createTenant = (body) =>
	call("/api/v1/platform/tenants", { method: "POST", body }, "tenant");

export const updateTenant = (id, body) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}`, { method: "PATCH", body }, "tenant");

export const setTenantStatus = (id, body) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/status`, { method: "POST", body }, "tenant");

// Provisioning (M3a). The API launches a Cloud Run Job and reads GCP back;
// it never creates a service account or sets a policy itself.
//
// 202, not 200: the resources do not exist yet when provisionTenant
// resolves. It returns {execution}, which getProvisionStatus polls.
export const provisionTenant = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/provision`, { method: "POST" }, "provision");

export const getProvisionStatus = (id, execution) =>
	call(
		`/api/v1/platform/tenants/${encodeURIComponent(id)}/provision?execution=${encodeURIComponent(execution)}`,
		undefined,
		"provision",
	);

// A report whose checks fail comes back 200, so this resolves rather than
// throwing. The caller renders the failures.
export const verifyTenant = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/verify`, { method: "POST" }, "verification");

// Data access: the API creates the tenant's Postgres role and its own
// BigQuery Cloud SQL connection, then binds roles/bigquery.connectionUser to
// the tenant service account on THAT connection only.
//
// Unlike provisionTenant this is synchronous (200, not 202) and runs in the
// API, not the Cloud Run job: a BigQuery connection authenticates with a
// stored database username and password, and the API is the only component
// that already holds control-plane database credentials. The generated
// password never appears in the response.
//
// Returns the tenant record, whose bqConnection is now set.
export const provisionTenantDataAccess = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/data-access`, { method: "POST" }, "tenant");

// Identity (M3b). A SECOND Cloud Run job, not the M3a one: realm
// administration implies creating a user in any realm, which is the same thing
// as minting a token for any tenant, so the API launches and reads but holds no
// Keycloak admin credential.
//
// 202 and {execution}, like provisionTenant -- the realm, its two clients, the
// audience mapper and the domain mapping do not exist yet when this resolves.
//
// `body` is {firstUserEmail, temporaryPassword} and both are optional: a realm
// may be provisioned before anyone is invited to it, and re-running to repair a
// mapper must not require inventing a second user. The password travels in the
// request BODY rather than the URL because a query parameter lands in access
// logs; the API then writes it to Secret Manager and passes the job only a
// name, because Cloud Run records job arguments in execution metadata.
export const provisionTenantIdentity = (id, body) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/identity`, { method: "POST", body }, "identity");

export const getIdentityStatus = (id, execution) =>
	call(
		`/api/v1/platform/tenants/${encodeURIComponent(id)}/identity?execution=${encodeURIComponent(execution)}`,
		undefined,
		"identity",
	);

// Same Check/Report shape as verifyTenant, so VerificationReport renders it
// unchanged, and the same 200-with-failures contract. Expect one check to fail
// on a freshly provisioned tenant: instantview.ai is delegated to a registrar
// GCP cannot write to, so the CNAME is a manual step and this report is what
// tells the operator it is still owed.
export const verifyTenantIdentity = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/identity/verify`, { method: "POST" }, "verification");

// Seeding (M3c). Synchronous and in the API, not a job: this writes the
// tenant's own control-plane rows -- the pipeline, its stages, the schedules,
// the owner's role grant -- and the API is where the control-plane database
// credentials already live.
//
// Returns a SeedReport: every row it touched, and whether it created it or
// found it already there. Re-running is safe and reports "existing" rather
// than creating a second copy; the one thing it refuses is seeding a tenant
// that already has users, because that would mint a second owner.
export const seedTenant = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/seed`, { method: "POST" }, "seed");

// Dataset build (M3c). A THIRD Cloud Run job, running dbt against the tenant's
// own dataset. 202 and {execution}, like provision and identity: the build is
// minutes long and the dataset is empty when this resolves.
//
// It runs `dbt snapshot` before `dbt run`, because the integration models
// ref() the snapshots -- run-first builds them from stale history.
//
// It has a precondition the button cannot show: dbt transforms the `stg_gcp_*`
// external tables that the Import stage creates from the tenant's own cloud
// billing export, and on a tenant that has never imported there is nothing to
// transform, so the build dies inside dbt. The API refuses that case with a
// 409 whose message says so (platformadmin.ErrNoSourceData), which arrives
// here as a plain err.message.
export const buildTenantDataset = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/dataset`, { method: "POST" }, "dataset");

export const getDatasetStatus = (id, execution) =>
	call(
		`/api/v1/platform/tenants/${encodeURIComponent(id)}/dataset?execution=${encodeURIComponent(execution)}`,
		undefined,
		"dataset",
	);

// Readiness (M3c). The nine checks that gate activation, in the same
// Check/Report shape verifyTenant returns so VerificationReport renders it
// unchanged.
//
// A GET, and a red report is a 200: "six of nine pass" is the answer to the
// question, not a failure to answer it. Every check reports the value it FOUND
// ("3 of 8 schedules"), not a boolean, so the report is a list of things to go
// do rather than a list of things to go investigate.
//
// The server computes this again inside activation and refuses on any red
// check, so this endpoint is what an operator consults on the way there, never
// the thing that decides.
export const getTenantReadiness = (id) =>
	call(`/api/v1/platform/tenants/${encodeURIComponent(id)}/readiness`, undefined, "readiness");
